import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import GoogleLoginButton from "../components/Auth/GoogleLoginButton";

/**
 * Production Login Page - Simplified and secure
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Form state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Get redirect path from location state
  const from = location.state?.from?.pathname || "/";

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Handle successful Google login
  const handleGoogleSuccess = async (authResult: any) => {
    try {
      setError(null);
      setSuccess('Authentication successful! Redirecting...');
      
      console.log('✅ LoginPage: Authentication successful', authResult);
      
      // Wait a moment for session data to be fully stored
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Trigger auth context refresh
      console.log('🔄 LoginPage: Dispatching auth:refresh-state event...');
      window.dispatchEvent(new CustomEvent('auth:refresh-state'));
      
      // Also try multiple refresh attempts
      setTimeout(() => {
        console.log('🔄 LoginPage: Dispatching second auth:refresh-state event...');
        window.dispatchEvent(new CustomEvent('auth:refresh-state'));
      }, 200);
      
      // Small delay to show success message
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 1000);
      
    } catch (error) {
      console.error('❌ LoginPage: Post-auth processing failed:', error);
      setError(error instanceof Error ? error.message : 'Login processing failed');
    }
  };

  // Handle Google login error
  const handleGoogleError = (error: any) => {
    console.error('❌ LoginPage: Google login error:', error);
    setError(error.error || error.message || 'Google sign-in failed');
    setSuccess(null);
  };

  return (
    <Container maxWidth="sm">
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 8,
        }}
      >
        <Typography component="h1" variant="h4" gutterBottom>
          Welcome Back
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Sign in with Google to continue to Hooliah
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ width: "100%", mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <GoogleLoginButton 
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: "center" }}>
          Don&apos;t have a Google account?{" "}
          <Typography component="span" variant="body2" color="primary">
            Create one for free at google.com
          </Typography>
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
          Note: You can browse outfits without logging in.{" "}
          <Typography 
            component="span" 
            variant="body2" 
            color="primary" 
            sx={{ cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            Continue as guest
          </Typography>
        </Typography>
      </Paper>
    </Container>
  );
};

export default LoginPage;